import React, { Component, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Careers from "@/pages/Careers";
import Contact from "@/pages/Contact";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-cream px-6" data-testid="error-boundary">
                    <div>
                        <h1 className="font-display text-5xl text-brand-red">WE HIT A BUMP.</h1>
                        <p className="mt-3 text-ink/70">Reload the page to get back on the road.</p>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

function ScrollAndLenis() {
    const { pathname } = useLocation();
    const lenisRef = useRef(null);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
        lenisRef.current = lenis;
        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (lenisRef.current) {
            lenisRef.current.scrollTo(0, { immediate: true });
        } else {
            window.scrollTo(0, 0);
        }
    }, [pathname]);

    return null;
}

function PageShell() {
    const { pathname } = useLocation();
    return (
        <div className="min-h-screen bg-cream text-ink flex flex-col">
            <Navbar />
            <motion.main
                key={pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.35 }}
                className="pt-[72px] md:pt-[103px] flex-1"
            >
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/careers" element={<Careers />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </motion.main>
            <Footer />
            <Toaster position="bottom-right" richColors />
        </div>
    );
}

function NotFound() {
    return (
        <div className="min-h-[60vh] flex flex-col justify-center px-6 lg:px-10 py-24" data-testid="not-found-page">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red">404 — Detour</p>
            <h1 className="font-display text-6xl md:text-8xl mt-4">LOST A LOAD?</h1>
            <p className="mt-4 text-ink/70 max-w-md">That page isn't on the manifest. Head back to home base.</p>
            <Link to="/" data-testid="not-found-home-link" className="mt-8 inline-flex w-fit items-center gap-2 border-[3px] border-ink bg-brand-red text-cream px-6 py-3.5 font-display uppercase text-lg shadow-[6px_6px_0_#111] hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[2px_2px_0_#111] transition-[transform,box-shadow] duration-200">
                Back to Memphis
            </Link>
        </div>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <ScrollAndLenis />
                <PageShell />
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
